import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Save, RotateCcw, AlertCircle, ArrowLeft, Building2 } from 'lucide-react';
import { useAuth } from '@clerk/react';
import { Button } from '@/components/ui/button.js';

const DEFAULT_EXTRACTION_PROMPT = `You are extracting structured information from an Australian government grant program page.

Extract the following fields as JSON:
- name: the official program name
- funder: the administering government agency
- status: one of "open", "closed", "forecast", or "archived" based on current application status
- open_date: application opening date in ISO 8601 format (null if not stated)
- close_date: application closing date in ISO 8601 format (null if not stated)
- funding_min: minimum grant amount in AUD as a number (null if not stated)
- funding_max: maximum grant amount in AUD as a number (null if not stated)
- eligibility: a plain-English summary of who is eligible (2–4 sentences)
- evidence_required: what applicants must provide as evidence (2–4 sentences)
- description: what the grant funds and its purpose (2–3 sentences)
- application_url: the direct URL to apply or register interest

Return ONLY valid JSON. No markdown, no commentary.`;

const DEFAULT_MATCHING_PROMPT = `You are assessing whether an R&D project is a strong candidate for a specific grant program.

Project TFA scope:
{tfa_scope}

Project activities:
{activities}

Grant details:
{grant_details}

Assess the alignment and return JSON:
- match_score: 0–100 (100 = perfect fit)
- alignment: what aspects of the project match the grant's eligibility and focus areas (2–3 sentences)
- gaps: what is missing or misaligned — evidence gaps, eligibility issues, or scope mismatches (2–3 sentences)
- recommendation: one of "strong_fit" (score ≥ 70), "partial_fit" (40–69), or "poor_fit" (< 40)

Return ONLY valid JSON. No markdown, no commentary.`;

interface GrantConfig {
  extractionPrompt: string;
  matchingPrompt: string;
  watchedGrantUuids: string[];
  watchedBusinessSlugs: string[];
}

interface OrgProfile {
  id: string;
  legalName: string;
  abn: string;
  address: string | null;
  industry: string | null;
  companySize: string | null;
  annualTurnover: string | null;
  description: string | null;
  updatedAt: string;
}

const COMPANY_SIZE_LABELS: Record<string, string> = {
  sole_trader: 'Sole trader',
  '2_10': '2–10 employees',
  '11_50': '11–50 employees',
  '51_200': '51–200 employees',
  '200_plus': '200+ employees',
};

const TURNOVER_LABELS: Record<string, string> = {
  under_1m: 'Under $1M',
  '1m_5m': '$1M – $5M',
  '5m_20m': '$5M – $20M',
  over_20m: 'Over $20M',
};

function OrgProfileSection({ getToken }: { getToken: () => Promise<string | null> }) {
  const [profile, setProfile] = useState<OrgProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);

  const [legalName, setLegalName] = useState('');
  const [abn, setAbn] = useState('');
  const [address, setAddress] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [annualTurnover, setAnnualTurnover] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const token = await getToken();
        const res = await fetch('/api/grants/org-profile', {
          headers: { Authorization: `Bearer ${token ?? ''}` },
        });
        if (res.status === 404) { setProfile(null); setEditing(true); return; }
        if (!res.ok) throw new Error('Failed');
        const json = await res.json() as { data: OrgProfile };
        setProfile(json.data);
        setLegalName(json.data.legalName);
        setAbn(json.data.abn);
        setAddress(json.data.address ?? '');
        setIndustry(json.data.industry ?? '');
        setCompanySize(json.data.companySize ?? '');
        setAnnualTurnover(json.data.annualTurnover ?? '');
        setDescription(json.data.description ?? '');
      } catch {
        setProfileError('Failed to load organisation profile');
      } finally {
        setLoadingProfile(false);
      }
    }
    void load();
  }, [getToken]);

  const saveProfile = async () => {
    const abnDigits = abn.replace(/\s/g, '');
    if (!/^\d{11}$/.test(abnDigits)) {
      setProfileError('ABN must be 11 digits (no spaces)');
      return;
    }
    if (!legalName.trim()) {
      setProfileError('Legal name is required');
      return;
    }
    setSaving(true);
    setProfileError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/grants/org-profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token ?? ''}`,
        },
        body: JSON.stringify({
          legalName: legalName.trim(),
          abn: abnDigits,
          address: address.trim() || undefined,
          industry: industry || undefined,
          companySize: companySize || undefined,
          annualTurnover: annualTurnover || undefined,
          description: description.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json() as { error: { message: string } };
        setProfileError(err.error.message);
        return;
      }
      const json = await res.json() as { data: OrgProfile };
      setProfile(json.data);
      setEditing(false);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch {
      setProfileError('Failed to save organisation profile');
    } finally {
      setSaving(false);
    }
  };

  if (loadingProfile) {
    return <div className="h-24 rounded-lg skeleton" />;
  }

  return (
    <section className="rounded-lg border p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-sm font-semibold">Organisation Profile</h2>
        </div>
        {profile && !editing && (
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>Edit</Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Used to pre-fill Application Aid answers. Keep this up to date so Claude has accurate information about your organisation.
      </p>

      {profileError && (
        <div className="flex items-center gap-2 text-destructive text-xs">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {profileError}
        </div>
      )}

      {!editing && profile ? (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Legal name</dt>
            <dd className="font-medium">{profile.legalName}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">ABN</dt>
            <dd className="font-medium font-mono">{profile.abn.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4')}</dd>
          </div>
          {profile.industry && (
            <div>
              <dt className="text-xs text-muted-foreground">Industry</dt>
              <dd>{profile.industry}</dd>
            </div>
          )}
          {profile.companySize && (
            <div>
              <dt className="text-xs text-muted-foreground">Company size</dt>
              <dd>{COMPANY_SIZE_LABELS[profile.companySize] ?? profile.companySize}</dd>
            </div>
          )}
          {profile.annualTurnover && (
            <div>
              <dt className="text-xs text-muted-foreground">Annual turnover</dt>
              <dd>{TURNOVER_LABELS[profile.annualTurnover] ?? profile.annualTurnover}</dd>
            </div>
          )}
          {profile.address && (
            <div className="sm:col-span-2">
              <dt className="text-xs text-muted-foreground">Address</dt>
              <dd>{profile.address}</dd>
            </div>
          )}
          {profile.description && (
            <div className="sm:col-span-2">
              <dt className="text-xs text-muted-foreground">Description</dt>
              <dd className="text-muted-foreground leading-relaxed">{profile.description}</dd>
            </div>
          )}
          <div className="sm:col-span-2">
            <dt className="text-xs text-muted-foreground">Last updated</dt>
            <dd className="text-muted-foreground">{new Date(profile.updatedAt).toLocaleDateString('en-AU')}</dd>
          </div>
        </dl>
      ) : (
        <div className="space-y-4">
          <div className="space-y-4">
            <div>
              <label htmlFor="org-legal-name" className="block text-xs font-medium mb-1">
                Legal name <span className="text-destructive">*</span>
              </label>
              <input
                id="org-legal-name"
                type="text"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Acme Pty Ltd"
              />
            </div>
            <div>
              <label htmlFor="org-abn" className="block text-xs font-medium mb-1">
                ABN <span className="text-destructive">*</span>
              </label>
              <input
                id="org-abn"
                type="text"
                value={abn}
                onChange={(e) => setAbn(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-background font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="12 345 678 901"
              />
            </div>
            <div>
              <label htmlFor="org-industry" className="block text-xs font-medium mb-1">Industry</label>
              <select
                id="org-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select industry</option>
                {['Technology', 'Biotech', 'Manufacturing', 'Clean Energy', 'Agriculture', 'Defence', 'Export', 'Research', 'Creative Industries', 'General'].map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="org-company-size" className="block text-xs font-medium mb-1">Company size</label>
              <select
                id="org-company-size"
                value={companySize}
                onChange={(e) => setCompanySize(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select size</option>
                {Object.entries(COMPANY_SIZE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="org-turnover" className="block text-xs font-medium mb-1">Annual turnover</label>
              <select
                id="org-turnover"
                value={annualTurnover}
                onChange={(e) => setAnnualTurnover(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select range</option>
                {Object.entries(TURNOVER_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="org-address" className="block text-xs font-medium mb-1">Address</label>
              <input
                id="org-address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full text-sm border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="123 Main St, Sydney NSW 2000"
              />
            </div>
          </div>
          <div>
            <label htmlFor="org-description" className="block text-xs font-medium mb-1">
              Organisation description
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              Brief description of what your company does and its primary R&D focus. Claude uses this when drafting application aid answers.
            </p>
            <textarea
              id="org-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full text-sm border rounded-lg px-3 py-2 bg-background resize-y focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="We develop ML-powered agricultural monitoring systems..."
            />
          </div>
          <div className="flex items-center gap-3">
            <Button variant="primary" onClick={() => void saveProfile()} disabled={saving}>
              <Save className="h-3.5 w-3.5" aria-hidden="true" />
              {saving ? 'Saving…' : 'Save Profile'}
            </Button>
            {profile && (
              <Button variant="ghost" onClick={() => { setEditing(false); setProfileError(null); }}>Cancel</Button>
            )}
            {profileSaved && <span className="text-sm text-emerald-600">Saved</span>}
          </div>
        </div>
      )}
    </section>
  );
}

export function GrantConfigPage() {
  const { getToken } = useAuth();
  const [extractionPrompt, setExtractionPrompt] = useState('');
  const [matchingPrompt, setMatchingPrompt] = useState('');
  const [watchedUuids, setWatchedUuids] = useState('');
  const [watchedSlugs, setWatchedSlugs] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const token = await getToken();
        const res = await fetch('/api/grants/config', {
          headers: { Authorization: `Bearer ${token ?? ''}` },
        });
        if (!res.ok) throw new Error('Failed to load config');
        const json = await res.json() as { data: GrantConfig };
        setExtractionPrompt(json.data.extractionPrompt || DEFAULT_EXTRACTION_PROMPT);
        setMatchingPrompt(json.data.matchingPrompt || DEFAULT_MATCHING_PROMPT);
        setWatchedUuids(json.data.watchedGrantUuids.join('\n'));
        setWatchedSlugs(json.data.watchedBusinessSlugs.join('\n'));
      } catch {
        setError('Failed to load grant config');
      }
    }
    void load();
  }, [getToken]);

  const save = async () => {
    if (!extractionPrompt.trim() || !matchingPrompt.trim()) {
      setError('Prompts cannot be empty');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const res = await fetch('/api/grants/config', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token ?? ''}`,
        },
        body: JSON.stringify({
          extractionPrompt,
          matchingPrompt,
          watchedGrantUuids: watchedUuids.split('\n').map((s) => s.trim()).filter(Boolean),
          watchedBusinessSlugs: watchedSlugs.split('\n').map((s) => s.trim()).filter(Boolean),
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-8">
      <Link href="/grants" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        Back to grants
      </Link>
      <div>
        <h1 className="text-lg font-semibold mb-1">Grants Settings</h1>
        <p className="text-sm text-muted-foreground">
          Manage the organisation profile and prompts used for grant analysis and project alignment.
        </p>
      </div>

      {/* Org Profile */}
      <OrgProfileSection getToken={getToken} />

      {/* Prompt config */}
      <section className="space-y-6">
        <h2 className="text-sm font-semibold">AI Prompts</h2>

        {error && (
          <div className="flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            {error}
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="extraction-prompt" className="text-sm font-medium">
              Grant Extraction Prompt
            </label>
            <button
              onClick={() => setExtractionPrompt(DEFAULT_EXTRACTION_PROMPT)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" aria-hidden="true" /> Restore default
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Used when analysing grant pages from business.gov.au and GrantConnect.
          </p>
          <textarea
            id="extraction-prompt"
            value={extractionPrompt}
            onChange={(e) => setExtractionPrompt(e.target.value)}
            rows={12}
            className="w-full text-xs font-mono border rounded-lg p-3 bg-muted/30 resize-y"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="matching-prompt" className="text-sm font-medium">
              Grant Matching / Comparison Prompt
            </label>
            <button
              onClick={() => setMatchingPrompt(DEFAULT_MATCHING_PROMPT)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" aria-hidden="true" /> Restore default
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            Used when comparing a project's TFA scope and activities against a grant's criteria. Use{' '}
            <code className="text-xs bg-muted px-1 rounded">{'{tfa_scope}'}</code>,{' '}
            <code className="text-xs bg-muted px-1 rounded">{'{activities}'}</code>, and{' '}
            <code className="text-xs bg-muted px-1 rounded">{'{grant_details}'}</code> as placeholders.
          </p>
          <textarea
            id="matching-prompt"
            value={matchingPrompt}
            onChange={(e) => setMatchingPrompt(e.target.value)}
            rows={14}
            className="w-full text-xs font-mono border rounded-lg p-3 bg-muted/30 resize-y"
          />
        </div>

        <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
          <p className="text-sm font-medium">Automatic grant sources</p>
          <p className="text-xs text-muted-foreground">
            Edison automatically discovers grants from these sources on a weekly schedule:
          </p>
          <ul className="text-xs text-muted-foreground space-y-1 pl-4 list-disc">
            <li><strong>business.gov.au</strong> — R&D Tax Incentive, EMDG, Industry Growth Program, CTCP</li>
            <li><strong>GrantConnect</strong> — web search discovery across all open R&D, tech, and SME programs + pinned UUIDs below</li>
            <li><strong>State grants (scrape)</strong> — VIC, ACT, NT, CSIRO Kick-Start from government portals</li>
            <li><strong>State grants (search)</strong> — NSW, QLD, SA, WA, TAS via AI-powered web search</li>
          </ul>
        </div>

        <div>
          <label htmlFor="watched-uuids" className="text-sm font-medium block mb-2">
            Pinned GrantConnect GoUuids
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Specific grants you always want refreshed — add the GoUuid from any grants.gov.au URL
            (e.g. <code className="text-xs bg-muted px-1 rounded">fb2ed0db-e898-4745-bbe9-2fdfe365b6f3</code>).
            These are synced in addition to the automatic discovery above.
          </p>
          <textarea
            id="watched-uuids"
            value={watchedUuids}
            onChange={(e) => setWatchedUuids(e.target.value)}
            rows={4}
            className="w-full text-xs font-mono border rounded-lg p-3 bg-muted/30 resize-y"
            placeholder="One GoUuid per line"
          />
        </div>

        <div>
          <label htmlFor="watched-slugs" className="text-sm font-medium block mb-2">
            Additional business.gov.au programs
          </label>
          <p className="text-xs text-muted-foreground mb-2">
            Add URL slugs for business.gov.au programs not already covered above
            (e.g. <code className="text-xs bg-muted px-1 rounded">advanced-manufacturing-fund</code>).
          </p>
          <textarea
            id="watched-slugs"
            value={watchedSlugs}
            onChange={(e) => setWatchedSlugs(e.target.value)}
            rows={3}
            className="w-full text-xs font-mono border rounded-lg p-3 bg-muted/30 resize-y"
            placeholder="One slug per line"
          />
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            onClick={() => void save()}
            disabled={saving}
          >
            <Save className="h-3.5 w-3.5" aria-hidden="true" />
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
          {saved && <span className="text-sm text-emerald-600">Saved</span>}
        </div>
      </section>
    </div>
  );
}
